import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/Logo";
import { RoleBadge } from "@/components/Badges";
import {
  LayoutDashboard, Package, ShoppingCart, Users, Wallet, BarChart3,
  Store, Megaphone, Sparkles, Mail, MessageSquare, Briefcase, Truck,
  Settings, FileQuestion, BookOpen, Tag, Receipt, LifeBuoy, LinkIcon,
  ShieldCheck, CreditCard, LogOut, Building2, Gem,
} from "lucide-react";

// Role-aware sidebar groups
function buildNav(roles = []) {
  const has = (r) => roles.includes(r) || roles.includes("super_admin");
  const items = [
    { to: "/app", label: "Overview", icon: LayoutDashboard, show: true, end: true },
  ];

  if (has("supplier") || has("merchant")) {
    items.push(
      { to: "/app/products", label: "Products", icon: Package, show: true },
      { to: "/app/orders", label: "Orders", icon: ShoppingCart, show: true },
      { to: "/app/coupons", label: "Coupons", icon: Tag, show: true },
    );
  }
  if (has("merchant") || has("creator")) {
    items.push({ to: "/app/store", label: "Store Builder", icon: Store, show: true });
  }
  if (has("merchant") || has("supplier") || has("creator")) {
    items.push(
      { to: "/app/crm", label: "CRM", icon: Users, show: true },
      { to: "/app/marketing/ai", label: "AI Marketing", icon: Sparkles, show: true },
      { to: "/app/marketing/email", label: "Email Campaigns", icon: Mail, show: true },
      { to: "/app/marketing/sms", label: "SMS Campaigns", icon: MessageSquare, show: true },
    );
  }
  if (has("affiliate") || has("creator")) {
    items.push({ to: "/app/affiliate", label: "Affiliate", icon: LinkIcon, show: true });
  }
  if (has("fulfillment_partner")) {
    items.push({ to: "/app/fulfillment", label: "Fulfillment Queue", icon: Truck, show: true });
  }
  if (has("service_provider")) {
    items.push(
      { to: "/app/services", label: "My Services", icon: Briefcase, show: true },
      { to: "/app/leads", label: "Lead Marketplace", icon: Megaphone, show: true },
    );
  }
  // Buyers also: leads (for requesting services)
  if (!has("service_provider")) {
    items.push({ to: "/app/leads", label: "Request Services", icon: Megaphone, show: true });
  }

  items.push(
    { to: "/app/wallet", label: "Wallet & Payouts", icon: Wallet, show: true },
    { to: "/app/gems", label: "Sell Gems", icon: Gem, show: true },
    { to: "/app/exchange/queue", label: "Gem Exchange Queue", icon: Gem, show: has("exchange_merchant") },
    { to: "/app/analytics", label: "Analytics", icon: BarChart3, show: has("supplier") || has("merchant") || has("super_admin") },
  );

  if (has("super_admin")) {
    items.push(
      { to: "/app/admin/users", label: "Users", icon: ShieldCheck, show: true },
      { to: "/app/admin/payments", label: "Payment Settings", icon: CreditCard, show: true },
      { to: "/app/admin/content", label: "FAQ & Guides", icon: BookOpen, show: true },
      { to: "/app/admin/health", label: "System Health", icon: Building2, show: true },
    );
  }

  items.push(
    { to: "/app/guides", label: "Role Guides", icon: BookOpen, show: true },
    { to: "/app/help", label: "Help Center", icon: LifeBuoy, show: true },
    { to: "/app/settings", label: "Settings", icon: Settings, show: true },
  );

  return items.filter((i) => i.show);
}

export default function DashboardLayout({ children, title, subtitle, actions }) {
  const { user, logout, primaryRole } = useAuth();
  const navigate = useNavigate();
  const items = buildNav(user?.roles || []);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 flex-col bg-primary text-primary-foreground border-r border-primary/40 sticky top-0 h-screen">
        <div className="px-5 h-16 flex items-center border-b border-white/10">
          <Link to="/" data-testid="sidebar-logo-link" className="flex items-center gap-2.5">
            <img src="https://customer-assets.emergentagent.com/job_vendora-os/artifacts/pwqbypxs_c95ab58d-87a7-4baa-ba81-1f6a714401b5.jpg" alt="V" className="w-8 h-8 object-contain rounded-sm bg-white p-0.5" />
            <span className="font-display font-black text-lg tracking-tight text-white">Vendora</span>
          </Link>
        </div>

        <div className="px-5 py-4 border-b border-white/10">
          <div className="text-[10px] font-mono tracking-[0.2em] text-white/50 uppercase">Signed in as</div>
          <div className="mt-1 text-sm font-medium text-white truncate">{user?.full_name || user?.email}</div>
          <div className="mt-2 flex flex-wrap gap-1">
            {(user?.roles || []).slice(0, 3).map((r) => (
              <span key={r} className="inline-flex items-center rounded-sm bg-white/10 border border-white/20 px-1.5 py-0.5 font-mono text-[9px] tracking-[0.15em] uppercase">
                {r.replace("_", " ")}
              </span>
            ))}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`sidebar-nav-${item.label.toLowerCase().replace(/\s|&/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                  isActive ? "bg-white text-primary font-semibold" : "text-white/80 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <item.icon size={16} strokeWidth={1.75} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-2 mx-3 mb-4 px-3 py-2 rounded-sm text-sm text-white/80 hover:bg-white/10 hover:text-white transition-colors"
          data-testid="sidebar-logout"
        >
          <LogOut size={16} /> Sign out
        </button>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 bg-card/95 backdrop-blur border-b border-border h-16 flex items-center px-6 justify-between">
          <div>
            <div className="label-eyebrow text-secondary">{primaryRole()?.replace("_", " ")} Portal</div>
            <h1 className="text-lg font-semibold text-foreground -mt-0.5">{title || "Overview"}</h1>
          </div>
          <div className="flex items-center gap-2">{actions}</div>
        </header>

        {subtitle && (
          <div className="px-6 pt-6 -mb-3">
            <p className="text-sm text-muted-foreground max-w-2xl">{subtitle}</p>
          </div>
        )}

        <div className="flex-1 p-6 lg:p-8">{children}</div>
      </div>
    </div>
  );
}
